import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearTaskArrivalToasts, dismissTaskArrivalToast, fetchTasks, receiveTaskDelegatedEvent } from '../../store/slices/taskSlice';
import {
  receiveUserNotification,
  pushHighlightedToast,
  dismissHighlightedToast,
  clearHighlightedToasts,
} from '../../store/slices/userNotificationSlice';
import notificationService from '../../services/notificationService';
import websocketService from '../../services/websocketService';
import { isHighlightedPopupEnabled } from '../../utils/highlightedNotificationPrefs';
import { splitReactionToMessage } from '../../utils/reactionConfig';
import { formatTaskDelegatedNotification, formatUserNotification } from '../../utils/notificationMessageUtils';
import PresignedImage from './PresignedImage';

const oneLine = (value) => String(value || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const senderLabel = (message, users = []) => {
  const fromMessage = oneLine(
    message?.senderName
    || [message?.firstName, message?.lastName].filter(Boolean).join(' ')
    || message?.username
    || ''
  );
  if (fromMessage) return fromMessage;
  const senderId = message?.senderId;
  if (!senderId || !Array.isArray(users)) return '';
  const user = users.find((item) => String(item.id) === String(senderId));
  if (!user) return '';
  return oneLine(
    [user.firstName, user.lastName].filter(Boolean).join(' ')
    || user.username
    || user.email
    || ''
  );
};

const popupLine = (sender, message) => {
  const who = oneLine(sender);
  const text = oneLine(message);
  if (!who) return text || 'New notification';
  if (!text) return who;
  const lowerWho = who.toLowerCase();
  const lowerText = text.toLowerCase();
  if (
    lowerText === lowerWho
    || lowerText.startsWith(`${lowerWho} `)
    || lowerText.startsWith(`${lowerWho}:`)
  ) {
    return text;
  }
  return `${who}: ${text}`;
};

const reactionPreview = (toast) => {
  const body = oneLine(toast?.body || '');
  const toIndex = body.lastIndexOf(' to ');
  if (toIndex >= 0) return body.slice(toIndex + 4).trim();
  return oneLine(toast?.ticketTitle || '');
};

const EMOJI_FONT = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Twemoji Mozilla",sans-serif';

const reactionParts = (toast) => {
  const body = oneLine(toast?.body || '');
  const toIndex = body.lastIndexOf(' to ');
  const fromBody = toIndex > 0 ? body.slice(0, toIndex).trim() : '';
  return splitReactionToMessage(
    toast.reactionKey || toast.reactionEmoji || toast.statusLabel || fromBody,
    reactionPreview(toast),
    toast.body || toast.title
  );
};

const HeadlineText = ({ item }) => {
  if (item.reactionGlyph) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span
          className="flex-shrink-0 text-[18px] leading-none"
          style={{ fontFamily: EMOJI_FONT }}
          role="img"
          aria-label="reaction"
        >
          {item.reactionGlyph}
        </span>
        <span className="min-w-0 truncate">to {item.reactionPreview || 'msg'}</span>
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      {item.thumbnailFileKey ? (
        <PresignedImage
          fileKey={item.thumbnailFileKey}
          alt=""
          className="h-8 w-8 flex-shrink-0 rounded-lg object-cover"
          style={{ height: 32, width: 32 }}
        />
      ) : null}
      <span className="min-w-0 flex-1 truncate whitespace-nowrap">
        {oneLine(item.headline) || 'New notification'}
      </span>
    </span>
  );
};

const openNotificationDetail = (detail) => {
  window.dispatchEvent(new CustomEvent('workloop:open-notification', { detail }));
};

const TaskArrivalToast = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toasts = useSelector((state) => state.tasks.taskArrivalToasts || []);
  const highlighted = useSelector((state) => state.userNotifications.highlightedToasts || []);
  const userId = useSelector((state) => state.auth.user?.id);
  const users = useSelector((state) => state.users.users || []);
  const playedIds = useRef(new Set());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return undefined;

    const handleTaskDelegatedEvent = (data) => {
      const eventType = String(data?.type || '').toUpperCase();
      const task = data?.task;
      const taskId = task?.id || data?.taskId;
      if (eventType !== 'TASK_DELEGATED' || !taskId) return;

      const payloadTask = task?.id ? task : { ...task, id: taskId };
      dispatch(receiveTaskDelegatedEvent({
        task: payloadTask,
        delegatorName: data.delegatorName,
        assignmentKind: data.assignmentKind,
      }));
      dispatch(fetchTasks());
    };

    const handleUserNotificationEvent = (data) => {
      if (!data || String(data.type || '').toUpperCase() !== 'USER_NOTIFICATION') return;
      if (userId && data.actorUserId && String(data.actorUserId) === String(userId)) return;
      const eventType = String(data.eventType || '').toUpperCase();
      if (eventType === 'EVENT_PUBLISHED' || String(data.type || '').toUpperCase() === 'EVENT_INVITE') {
        window.dispatchEvent(new CustomEvent('workloop:event-invite', { detail: data }));
      } else if (eventType === 'TASK_ASSIGNED' && data.taskId) {
        handleTaskDelegatedEvent({
          type: 'TASK_DELEGATED',
          task: { id: data.taskId, title: data.ticketTitle || data.body || data.title },
          delegatorName: data.actorName,
          assignmentKind: 'DELEGATED',
        });
      } else if (isHighlightedPopupEnabled()) {
        dispatch(pushHighlightedToast(data));
      }
      dispatch(receiveUserNotification(data));
    };

    const subscribe = () => {
      websocketService.subscribeToTasks(handleTaskDelegatedEvent);
      websocketService.subscribeToTopic(`/topic/tasks/${userId}`, handleTaskDelegatedEvent);
      websocketService.subscribeToTopic(`/user/queue/notifications`, handleUserNotificationEvent);
      websocketService.subscribeToTopic(`/topic/notifications/${userId}`, handleUserNotificationEvent);
    };

    subscribe();
    const retryId = window.setInterval(subscribe, 4000);
    return () => {
      window.clearInterval(retryId);
    };
  }, [dispatch, userId]);

  const items = useMemo(() => [
    ...toasts.map((toast) => ({
      id: toast.id,
      group: 'task',
      headline: formatTaskDelegatedNotification(toast).line,
      thumbnailFileKey: null,
      open: () => {
        dispatch(dismissTaskArrivalToast(toast.id));
        if (!toast?.task?.id) return;
        navigate('/chat');
        window.setTimeout(() => {
          openNotificationDetail({ eventType: 'TASK_ASSIGNED', taskId: toast.task.id });
        }, 80);
      },
      dismiss: () => dispatch(dismissTaskArrivalToast(toast.id)),
    })),
    ...highlighted
      .filter((toast) => !(toast.taskId && toasts.some((item) => String(item.task?.id) === String(toast.taskId))))
      .map((toast) => {
        const eventType = String(toast.eventType || '').toUpperCase();
        const assignmentLine = (eventType === 'TASK_ASSIGNED' || eventType === 'WORKFLOW_ASSIGNED')
          ? formatUserNotification(toast).line
          : null;
        const isReaction = eventType === 'MESSAGE_REACTION';
        const reaction = isReaction ? reactionParts(toast) : null;
        return {
        id: toast.id,
        group: 'highlight',
        headline: isReaction
          ? reaction.line
          : (assignmentLine || popupLine(
            toast.actorName || senderLabel({ senderId: toast.senderId }, users),
            toast.body || toast.ticketTitle || toast.title
          )),
        reactionGlyph: reaction?.glyph || '',
        reactionPreview: reaction?.preview || '',
        thumbnailFileKey: toast.thumbnailFileKey || '',
        open: () => {
          dispatch(dismissHighlightedToast(toast.id));
          navigate('/chat');
          window.setTimeout(() => {
            const eventType = String(toast.eventType || '').toUpperCase();
            if (eventType === 'MENTION' || eventType === 'MESSAGE' || eventType === 'MESSAGE_REACTION') {
              if (toast.sourceMessageId) {
                window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
                  detail: {
                    messageId: toast.sourceMessageId,
                    channelId: toast.sourceChannelId,
                  },
                }));
              }
              return;
            }
            if (eventType.startsWith('MEETING_')) {
              if (toast.meetingLink && eventType === 'MEETING_REMINDER') {
                window.open(toast.meetingLink, '_blank', 'noopener,noreferrer');
              }
              if (toast.sourceChannelId && toast.sourceMessageId) {
                window.dispatchEvent(new CustomEvent('workloop:go-to-message', {
                  detail: { messageId: toast.sourceMessageId, channelId: toast.sourceChannelId },
                }));
                return;
              }
              window.dispatchEvent(new CustomEvent('workloop:open-calendar'));
              return;
            }
            if (eventType === 'LEAVE_APPROVAL_REQUEST') {
              window.dispatchEvent(new CustomEvent('workloop:open-team-attendance'));
              return;
            }
            openNotificationDetail({
              eventType: toast.eventType,
              sourceChannelId: toast.sourceChannelId,
              sourceMessageId: toast.sourceMessageId,
              taskId: toast.taskId,
              notificationChannelId: toast.notificationChannelId,
            });
          }, 80);
        },
        dismiss: () => dispatch(dismissHighlightedToast(toast.id)),
        };
      }),
  ], [dispatch, highlighted, navigate, toasts, users]);

  const soundKey = items.map((item) => item.id).join('|');
  useEffect(() => {
    items.forEach((item) => {
      if (!item?.id || playedIds.current.has(item.id)) return;
      playedIds.current.add(item.id);
      if (!notificationService.isSpeechEnabled()) {
        notificationService.playNotificationSound();
      }
    });
  }, [items, soundKey]);

  useEffect(() => {
    if (!items.length) {
      setOpen(false);
      return;
    }
    if (items.length > 1) setOpen(true);
  }, [items.length]);

  if (!items.length) return null;

  const count = items.length;
  const latest = items[0];

  const dismissAll = () => {
    dispatch(clearTaskArrivalToasts());
    dispatch(clearHighlightedToasts());
  };

  return (
    <div className="fixed bottom-6 right-5 z-[4000] pointer-events-none">
      <div className="pointer-events-auto w-[360px] overflow-hidden rounded-[22px] border border-white/15 bg-gradient-to-b from-slate-900/95 to-slate-950/95 text-white shadow-[0_24px_60px_rgba(15,23,42,0.55)] backdrop-blur-2xl">
        <div className="h-1 w-full bg-gradient-to-r from-sky-400 via-indigo-400 to-amber-400" />
        <div className="relative flex items-center gap-3 px-4 py-3 pr-3">
          <button
            type="button"
            onClick={() => (count === 1 ? latest.open() : setOpen((value) => !value))}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
          >
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 text-sm font-bold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]">
              {count}
            </span>
            <span className="min-w-0 flex-1 text-[14px] font-semibold leading-5 tracking-tight">
              <HeadlineText item={latest} />
            </span>
          </button>
          <button
            type="button"
            onClick={dismissAll}
            className="flex-shrink-0 rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white/90 hover:bg-white/20"
            aria-label="Hide all"
          >
            Hide all
          </button>
        </div>

        {(open || count > 1) && open ? (
          <div className="max-h-64 overflow-y-auto border-t border-white/10 bg-black/20">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-stretch border-b border-white/10 last:border-b-0 hover:bg-white/10"
              >
                <button
                  type="button"
                  onClick={item.open}
                  className="flex min-w-0 flex-1 items-center gap-2.5 px-4 py-2.5 text-left"
                >
                  <span className={`h-2 w-2 flex-shrink-0 rounded-full ${
                    item.group === 'task' ? 'bg-sky-400' : 'bg-amber-400'
                  }`}
                  />
                  <span className="min-w-0 flex-1 text-[13px] font-medium">
                    <HeadlineText item={item} />
                  </span>
                </button>
                <button
                  type="button"
                  onClick={item.dismiss}
                  className="flex w-9 flex-shrink-0 items-center justify-center text-white/40 hover:text-white"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TaskArrivalToast;
